<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useAcl } from "vue-simple-acl";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import type { CloseEvent } from "~/core/models/CloseEvent.interface";
import { CUSTOM_INPUT_PARAMETERS_NAMES } from "~/modules/campaign/models/CampaignOperationMgr.enum";
import type { CampaignOperationMgr } from "~/modules/campaign/models/CampaignOperationMgr.interface";
import type { CampaignOperationParameter } from "~/modules/campaign/models/CampaignOperationParameter.interface";
import { OrganizationType } from "~/modules/organization/models/OrganizationType.enum";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import ILxcArrowRight from "~icons/lxc/arrow-right";

const props = defineProps<{
  campaign?: CampaignOperationMgr;
}>();

const emit = defineEmits(["reloadCampaign"]);

const { t } = useI18n();
const { can } = useAcl();
const { currentOrganization } = storeToRefs(useOrganizationStore());

const showUserForm: Ref<boolean> = ref<boolean>(false);
const currentSelectedUser: Ref<string | undefined> = ref(undefined);

const createdBy: ComputedRef<string> = computed(() => {
  let result: string = props.campaign?.createdBy || "-";
  if (props.campaign?.createdByFirstName || props.campaign?.createdByLastName) {
    result = `${props.campaign?.createdByFirstName} ${props.campaign?.createdByLastName}`;
  }
  return result;
});

function onCloseCanvas(event: CloseEvent) {
  if (event.edited) {
    emit("reloadCampaign");
  }
}

function onCreatedByClick() {
  showUserForm.value = true;
  currentSelectedUser.value = props.campaign?.createdBy;
}

const campaignOperationParameterWithInclusionPeriods: ComputedRef<
  CampaignOperationParameter | undefined
> = computed(() => {
  if (
    props.campaign?.operationParametersList &&
    props.campaign?.operationParametersList.length > 0
  ) {
    return props.campaign.operationParametersList.find(
      (operationParameter) =>
        operationParameter.customInputParameters?.inclusionPeriods,
    );
  }
});
</script>

<template>
  <lxc-card v-bind="$attrs">
    <h5 class="!mt-0 !mb-4">
      {{ t("campaign.details.characteristics.title") }}
    </h5>
    <div class="flex gap-4">
      <div class="flex-1">
        <p class="font-medium">
          <span class="mr-1">{{
            t("campaign.details.characteristics.createdBy")
          }}</span>
          <a
            v-if="can(ACLRoles.CAN_READ_USER) && props.campaign?.createdBy"
            href="#"
            type="borderless"
            @click="onCreatedByClick"
          >
            {{ createdBy }}
          </a>
          <span v-else>{{ createdBy }}</span>
        </p>
        <p
          v-if="
            campaign?.operationTypes?.length &&
            campaign?.operationTypes?.length > 0
          "
          class="text-primary-800 text-xs font-medium align-middle"
        >
          {{ t("campaign.details.characteristics.operationType") }}

          <campaign-operation-type
            :operation-types="campaign?.operationTypes"
          />
          <span
            v-if="
              campaign &&
              campaign?.operationParametersList &&
              campaign?.operationParametersList?.length === 1 &&
              campaign?.operationParametersList[0].customInputParameters &&
              campaign?.operationParametersList[0].customInputParameters[
                CUSTOM_INPUT_PARAMETERS_NAMES.TARGET_VERSION
              ]
            "
            class="whitespace-nowrap"
          >
            <i-lxc-arrow-right
              v-if="campaign.operationTypes?.length > 0"
              class="inline-block w-3 h-3 mx-2 text-gray-700"
            /><span class="inline-block">{{
              campaign?.operationParametersList[0].customInputParameters[
                CUSTOM_INPUT_PARAMETERS_NAMES.TARGET_VERSION
              ]
            }}</span>
          </span>
        </p>
        <div class="pt-4">
          <campaign-timeline :campaign-operation-mgr="campaign" />
        </div>
      </div>
      <div v-if="campaignOperationParameterWithInclusionPeriods" class="flex-1">
        <campaign-inclusion-periods
          :campaign-operation-parameter-with-inclusion-periods="
            campaignOperationParameterWithInclusionPeriods
          "
        />
      </div>
    </div>
  </lxc-card>
  <user-form
    v-model:show="showUserForm"
    :organization-code="currentOrganization?.code"
    :organization-type="OrganizationType.OPERATOR"
    :user-login="currentSelectedUser"
    @close="onCloseCanvas"
  ></user-form>
</template>
